import { type FC, useCallback } from 'react'

import { useUnit } from 'effector-react'

import { Button } from '@/components/ui/button'
import { Dialog } from '@/components/ui/dialog'
import {
  $nameToDisplay,
  $summary,
  exportSummaryFx,
} from '@/models/chattingUI/chatSummary.ts'
import { $fan } from '@/models/chattingUI/chats/chats.ts'
import ChatSummaryIcon from '@/modules/chatting/icons/summarize.svg'
import { CreatorAvatar } from '@/modules/creators/ui/CreatorAvatar'

import type { DialogProps } from '../types'

export interface Props extends DialogProps {
  /**
   * Formatted date.
   */
  date: string
}

export const ChatSummaryDialog: FC<Props> = ({ isOpen, onClose, date }) => {
  const fan = useUnit($fan)!
  const summary = useUnit($summary)
  const nameToDisplay = useUnit($nameToDisplay)
  const handleExportClick = useCallback(() => {
    exportSummaryFx(date)
  }, [date])

  return (
    <Dialog
      show={isOpen}
      onClose={onClose}
      bigDialog
      panelClassName="lg:max-w-[533px]"
    >
      <div className="space-y-6">
        <Dialog.Header>
          <Dialog.Title>Summarize Chat</Dialog.Title>
          <Dialog.Description className="mx-auto block max-w-[407px] text-center">
            You can summarize the chat from a specific date to the present.
          </Dialog.Description>
        </Dialog.Header>
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-3">
            <CreatorAvatar
              size={40}
              avatarSrc={
                fan.avatarThumbs?.c50 ?? fan.avatarThumbs?.c144 ?? fan.avatar
              }
              name={fan.displayName ?? fan.name}
            />
            <p className="font-semibold leading-[26px]">{nameToDisplay}</p>
          </div>
          <p className="text-sm">{date}</p>
        </div>
        <div className="space-y-3 rounded-md border border-border-secondary p-3">
          <div className="flex gap-2">
            <p className="font-semibold leading-[26px]">Summarized Chat</p>
            <ChatSummaryIcon className="text-primary-500" />
          </div>
          <p className="text-sm">{summary}</p>
        </div>
        <Dialog.ClosableFooter onClose={onClose}>
          <Button onClick={handleExportClick}>Export</Button>
        </Dialog.ClosableFooter>
      </div>
    </Dialog>
  )
}
